import Store from '@ember-data/store';
import Service, { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { type SelectOption } from 'donor-elf-web/lib/form-component-utils';
import TaskActionModel from 'donor-elf-web/models/task-action';
import TaskMediumModel from 'donor-elf-web/models/task-medium';
import TaskResultModel from 'donor-elf-web/models/task-result';
import TaskUserModel from 'donor-elf-web/models/task-user';
import V2TaskModel from 'donor-elf-web/models/v2-task';
import AbilitiesService from './abilities';
import CachedDataset from './cached-dataset';
import CurrentUser from './current-user';
import FundsService from './funds-service';
import JournalService from './journal-service';

export default class TaskService extends Service {
  @service declare abilities: AbilitiesService;
  @service declare cachedDataset: CachedDataset;
  @service declare currentUser: CurrentUser;
  @service declare fundsService: FundsService;
  @service declare journalService: JournalService;
  @service declare store: Store;

  @tracked actions: TaskActionModel[] = [];
  @tracked mediums: TaskMediumModel[] = [];
  @tracked results: TaskResultModel[] = [];
  @tracked users: TaskUserModel[] = [];

  @cached
  get actionsById() {
    let byIds: { [id: string]: TaskActionModel } = {};
    for (let action of this.actions) {
      byIds[action.id] = action;
    }
    return byIds;
  }

  @cached
  get actionLabelsById() {
    let labels: { [id: string]: string } = {};
    for (let action of this.actions) {
      labels[action.id] = action.label;
    }
    return labels;
  }

  @cached
  get actionsByLabel() {
    let byLabels: { [label: string]: TaskActionModel } = {};
    for (let action of this.actions) {
      byLabels[action.label] = action;
    }
    return byLabels;
  }

  @cached
  get actionOptions(): SelectOption[] {
    return [
      {
        label: '',
        val: null,
      } as SelectOption,
    ].concat(
      this.actions.map((x) => {
        return {
          label: x.label,
          val: x.id,
        };
      }),
    );
  }

  @cached
  get mediumsByLabel() {
    let byLabels: { [label: string]: TaskMediumModel } = {};
    for (let medium of this.mediums) {
      byLabels[medium.label] = medium;
    }
    return byLabels;
  }

  @cached
  get mediumLabelsById() {
    let labels: any = {};
    for (let medium of this.mediums) {
      labels[medium.id] = medium.label;
    }
    return labels;
  }

  @cached
  get mediumOptions(): SelectOption[] {
    return [
      {
        label: '',
        val: null,
      } as SelectOption,
    ].concat(
      this.mediums.map((x) => {
        return {
          label: x.label,
          val: x.id,
        };
      }),
    );
  }

  @cached
  get resultsById() {
    let byIds: { [resultId: string]: TaskResultModel } = {};
    for (let result of this.results) {
      byIds[result.id] = result;
    }
    return byIds;
  }

  @cached
  get resultLabelsById() {
    let labels: { [resultId: string]: string } = {};
    for (let result of this.results) {
      labels[result.id] = result.label;
    }
    return labels;
  }

  @cached
  get availableResultsByActionId() {
    let resultsByActionId: { [actionId: string]: TaskResultModel[] } = {};
    for (let action of this.actions) {
      resultsByActionId[action.id] = action.resultIds.map(
        (resultId) => this.resultsById[resultId],
      );
    }
    return resultsByActionId;
  }

  @cached
  get usersByFundId() {
    let usersByFundId: { [fundId: string]: TaskUserModel[] } = {};
    for (let user of this.users) {
      for (let fundId of user.fundIds) {
        if (usersByFundId[fundId] == null) {
          usersByFundId[fundId] = [];
        }
        usersByFundId[fundId].push(user);
      }
    }
    return usersByFundId;
  }

  @cached
  get usersById() {
    let byId: { [userId: string]: TaskUserModel } = {};
    for (let user of this.users) {
      byId[user.id] = user;
    }
    return byId;
  }

  @cached
  get userLabelsById() {
    let labels: { [userId: string]: string } = {};
    for (let user of this.users) {
      labels[user.id] = user.displayName;
    }
    return labels;
  }

  // always call this method to create a new task record
  createRecord(attrs = {}): V2TaskModel {
    let assignedUserIds = null;
    if (this.abilities.hasAbility('assign_tasks')) {
      assignedUserIds = [this.currentUser.id];
    }
    return this.store.createRecord(
      'v2-task',
      Object.assign({ assignedUserIds }, attrs),
    );
  }

  copyRecord(task: V2TaskModel): V2TaskModel {
    return this.createRecord({
      contactId: task.contactId,
      actionId: task.actionId,
      mediumId: task.mediumId,
      resultId: task.resultId,
      summary: task.summary,
      notes: task.notes,
      dueDate: task.dueDate,
      completedDate: task.completedDate,
      assignedUserIds: task.assignedUserIds,
      journalStageId: task.journalStageId,
    });
  }

  // copies the task and pastes it to all the contactIds
  async copyAndPasteAll({
    task,
    contactIds,
  }: {
    task: V2TaskModel;
    contactIds: string[];
  }): Promise<V2TaskModel[]> {
    let pasteRecord = this.store.createRecord('v2-task-paste', {
      copiedTaskId: task.id,
      contactIds: contactIds,
      actionId: task.actionId,
      mediumId: task.mediumId,
      resultId: task.resultId,
      summary: task.summary,
      notes: task.notes,
      dueDate: task.dueDate,
      completedDate: task.completedDate,
      assignedUserIds: task.assignedUserIds,
      journalStageId: task.journalStageId,
    });
    await pasteRecord.save();
    return (
      await this.store.query('v2-task', {
        filter: { task_group_id: pasteRecord.taskGroupId! },
      })
    ).slice();
  }

  _loadPromise?: Promise<void>;
  load({ forceLoad }: { forceLoad?: boolean } = {}) {
    if (forceLoad) {
      this._loadPromise = undefined;
    }
    if (this._loadPromise != null) {
      return this._loadPromise;
    }

    let promises = [];
    if (this.abilities.hasAbility('assign_tasks')) {
      promises.push(
        this.fundsService.getAllFundsInfo().then(async () => {
          this.users = await this.cachedDataset.forModel('task-user').load();
        }),
      );
    }

    promises.push(
      this.store
        .findRecord('task-association', 1, { reload: true })
        .then((record) => {
          this.actions = record.taskActions.slice();
          this.mediums = record.taskMediums.slice();
          this.results = record.taskResults.slice();
          return this.journalService.load();
        }),
    );

    return (this._loadPromise = Promise.allSettled(promises).then(() => {
      return undefined;
    }));
  }
}
